import { ChangeEvent } from 'react';
import { format, isAfter, parse } from 'date-fns';

import {
    markFieldFocus,
    trackSelection,
    uniqueIdTransfer,
    useFieldValidation,
} from '@/apiCalls/uniqueIDTransferHelper';
import { trimClassname } from '@/utils/styleHelper';

import { EEventType } from '../eventType';
import { FieldLabel } from '../FieldLabel';

export interface ITimeInput {
    title: string;
    placeholder: string;
    onChange: (value: Date) => void;
    showErrors: boolean;
    id?: string;
    disabled?: boolean;
    value?: Date;
    minTime?: Date;
    maxTime?: Date;
    timeFormat?: string; // 'HH:mm' for 24-hour format or 'hh:mm a' for 12-hour format
    tooltipContent?: string;
    /** Field wrappers are required by default; optional fields opt out. */
    required?: boolean;
}

export function TimeInput({
    title,
    placeholder,
    id,
    value,
    showErrors,
    disabled = false,
    onChange,
    minTime,
    maxTime,
    timeFormat = 'HH:mm', // Default to 24-hour format
    tooltipContent,
    required = true,
}: ITimeInput): JSX.Element {
    useFieldValidation(id, showErrors);

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;
        // Parse the time string using today's date as a base
        const baseDate = new Date();
        let time = parse(value, timeFormat, baseDate);

        if (maxTime && isAfter(time, maxTime)) {
            time = maxTime;
        }

        onChange(time);

        uniqueIdTransfer({
            eventType: EEventType.INPUT,
            applicationData: { id: id },
        });
        trackSelection(id);
    };

    function getDisplayValue(): string {
        if (!value) return '';
        // Same guard as DateInput, and for the same reason: an INVALID Date is still a
        // truthy object, so `!value` misses it and date-fns' format() throws
        // RangeError: Invalid time value during render — which does not fail politely, it
        // takes down the React tree and leaves the applicant a blank widget. Coerced
        // because the prop is typed as a Date but callers pass the raw stored value,
        // which can be a string.
        const asDate = value instanceof Date ? value : new Date(value);
        if (isNaN(asDate.getTime())) return '';
        if (maxTime && isAfter(value, maxTime))
            return format(maxTime, timeFormat);
        return format(value, timeFormat);
    }

    return (
        <div data-testid='input' className='py-2 w-full'>
            <FieldLabel
                title={title}
                tooltipContent={tooltipContent}
                required={required}
            />
            <input
                className={trimClassname(
                    `appearance-none border rounded-[10px] w-full p-2 text-textPrimary leading-tight h-11 inputButtonFocus placeholderStyle ${
                        getDisplayValue() === '' ? 'emptyDateTime' : ''
                    } ${showErrors ? errorClass : ''} ${
                        disabled
                            ? 'hover:none cursor-disabled'
                            : 'hover:shadow '
                    }`
                )}
                id={id}
                type='time'
                data-testid='inputField'
                placeholder={placeholder}
                value={getDisplayValue()}
                onChange={handleChange}
                onFocus={(): void => markFieldFocus(id)}
                disabled={disabled}
                min={minTime ? format(minTime, timeFormat) : undefined}
                max={maxTime ? format(maxTime, timeFormat) : undefined}
                pattern={timeFormat}
                onInput={handleChange}
                onBlur={handleChange}
            />
        </div>
    );
}
export const errorClass = 'error';
